// Bestellvorschlag je Standort und Lieferant – und die Anforderungen der Filialen, die darin erscheinen.
// Der Vorschlag wird bei jedem Aufruf frisch aus dem Bestandskern gerechnet (nichts Veraltetes liegt herum);
// was daraus bestellt wird, steht mit seiner Grundlage in `reorder_suggestion` – ein späterer Leser sieht, warum.
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zDay, zLocationRef, zNote, zReason, zSupplierRef, zUnitInfo, zUnitQty } from './common'

const READ = 'purchasing.suggestion.read'

export const NEED_REASONS = ['below_reorder', 'requisition', 'covered', 'sufficient'] as const

export const zRequisitionRef = z.object({
  id: zId,
  location: zLocationRef,
  /** angeforderte Menge in Tausendstel der Basiseinheit */
  requestedQty: z.int(),
  requestedByName: z.string(),
  requestedAt: z.date(),
  wantedDate: z.string().nullable(),
  note: z.string().nullable(),
})

export const zOpenOrderRef = z.object({
  id: zId,
  number: z.string().nullable(),
  status: z.string(),
  statusLabel: z.string(),
  deliveryDate: z.string().nullable(),
  /** offene Menge dieser Bestellung in Tausendstel der Basiseinheit */
  openQty: z.int(),
})

export const zSuggestionLine = z.object({
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  /** Bestelleinheit des Lieferanten – in ihr steht `proposedQty` */
  orderUnit: zUnitInfo,
  /** Lagergebinde – in ihm zeigt die Oberfläche Bestände („152 Ka“) */
  stockUnit: zUnitInfo,
  // Grundlage, alles in Tausendstel der Basiseinheit
  onHand: z.int(),
  available: z.int(),
  reserved: z.int(),
  blocked: z.int(),
  /** offene Mengen bestellter Bestellungen */
  incoming: z.int(),
  /** unterwegs aus Umlagerungen zu diesem Standort */
  inTransitQty: z.int(),
  /** in Bestellungen, die noch nicht bestellt sind (Entwurf, zur Freigabe, freigegeben) */
  draftedQty: z.int(),
  /** angefordert von Filialen (Summe der offenen Anforderungen) */
  requestedQty: z.int(),
  reorderQty: z.int(),
  targetQty: z.int(),
  minQty: z.int(),
  /** verfügbar + bestellt + unterwegs + in Vorbereitung */
  coveredQty: z.int(),
  /** Abgang je Tag im Schnitt der letzten 28 Tage (Verkauf, Umlagerung an Filialen); 0 = keine Bewegung */
  perDayQty: z.int(),
  /** Reichweite des verfügbaren Bestands in Tagen; null ohne Abgang */
  rangeDays: z.int().nullable(),
  /** Vorschlag in Tausendstel der Bestelleinheit (immer ganze Bestelleinheiten); 0 = nichts zu bestellen */
  proposedQty: z.int(),
  /** bis zu dieser Menge (Tausendstel der Bestelleinheit) ließe sich zusätzlich bis zum Zielbestand auffüllen */
  fillUpQty: z.int(),
  reason: z.enum(NEED_REASONS),
  /** ein Satz zum Warum: „Unter Meldebestand: verfügbar 96 Ka, Meldebestand 128 Ka, Ziel 320 Ka.“ */
  reasonText: z.string(),
  /** verfügbar unter dem Mindestbestand */
  urgent: z.boolean(),
  requisitions: z.array(zRequisitionRef),
  openOrders: z.array(zOpenOrderRef),
  /** Einkaufspreis in Cent netto je `pricePerQty` der Bestelleinheit – nur mit „Einstandspreise sehen“ */
  priceCents: z.int().optional(),
  pricePerQty: z.int().optional(),
  /** Wert des Vorschlags in Cent netto */
  valueCents: z.int().optional(),
})
export type SuggestionLine = z.output<typeof zSuggestionLine>

export const zSuggestionGroup = z.object({
  supplier: zSupplierRef,
  /** Lieferzeit in Werktagen */
  leadTimeDays: z.int(),
  /** 1 = Montag … 7 = Sonntag; leer = jeder Werktag */
  orderWeekdays: z.array(z.int()),
  deliveryWeekdays: z.array(z.int()),
  /** email | phone | portal | edi | fax | in_person */
  orderChannel: z.string().nullable(),
  /** nächster Bestelltag (heute eingeschlossen) und frühester Liefertag danach */
  orderDate: z.string(),
  deliveryDate: z.string(),
  orderToday: z.boolean(),
  lines: z.array(zSuggestionLine),
  /** Zeilen mit Vorschlag größer null */
  proposalCount: z.int(),
  // nur mit „Einstandspreise sehen“:
  minOrderValueCents: z.int().nullable().optional(),
  valueCents: z.int().optional(),
  /** true: der Vorschlag liegt unter dem Mindestbestellwert des Lieferanten */
  belowMinimum: z.boolean().optional(),
  missingCents: z.int().optional(),
  /** Vorschlag zum Auffüllen bis zum Mindestbestellwert: die dringendsten Artikel zuerst (zusätzliche Menge) */
  fillUp: z.array(z.object({ itemId: zId, addQty: z.int(), addValueCents: z.int() })).optional(),
  /** true: auch mit Auffüllen bis zum Zielbestand wird der Mindestbestellwert nicht erreicht */
  fillUpShort: z.boolean().optional(),
})
export type SuggestionGroup = z.output<typeof zSuggestionGroup>

export const suggestionPropose = defineContract({
  name: 'purchasing.suggestion.propose',
  title: 'Bestellvorschlag anzeigen',
  description: 'Rechnet für einen Standort, was bei welchem Lieferanten zu bestellen ist – mit Grundlage je Artikel.',
  llm: {
    description:
      'Rechnet den Bestellvorschlag eines Standorts frisch aus dem Bestand: je Lieferant die Artikel, deren gedeckter ' +
      'Bestand (verfügbar + bestellt + unterwegs + in Bestellentwürfen) unter dem Meldebestand liegt, aufgefüllt bis ' +
      'zum Zielbestand und aufgerundet auf die Bestelleinheit des Lieferanten. Dazu Lieferzeit, nächster Bestell- und ' +
      'Liefertag, Mindestbestellwert (mit Vorschlag zum Auffüllen) und offene Anforderungen der Filialen. ' +
      '`proposedQty` steht in Tausendstel der Bestelleinheit (1 Palette = 1000), alle Bestände in Tausendstel der ' +
      'Basiseinheit. Mit `supplierId` nur ein Lieferant – die Antwort auf „Was müssen wir bei diesem Lieferanten bestellen?“. ' +
      'Verändert nichts. Daraus bestellen: purchasing.suggestion.order. Nachschub aus dem eigenen Lager für eine ' +
      'Filiale ist eine Umlagerung (inventory.transfer.propose), keine Bestellung.',
    keywords: ['Bestellvorschlag', 'nachbestellen', 'Was müssen wir bestellen', 'Meldebestand', 'Zielbestand', 'Mindestbestellwert', 'Disposition', 'Lieferant', 'Bedarf'],
  },
  input: z.strictObject({
    locationId: zId,
    supplierId: zId.optional(),
    /** auch Artikel zeigen, die unter Meldebestand liegen, aber durch offene Bestellungen gedeckt sind (Standard: ja) */
    includeCovered: z.boolean().default(true),
  }),
  output: z.object({
    location: zLocationRef,
    /**
     * Der Standort wird von einem anderen beliefert (Filiale ← Zentrallager): Nachschub läuft dort als Umlagerung,
     * der Vorschlag rechnet KEINEN Bedarf aus dem Meldebestand und zeigt nur die Anforderungen des Standorts.
     */
    suppliedBy: zLocationRef.nullable(),
    date: z.string(),
    groups: z.array(zSuggestionGroup),
    /** Artikel unter Meldebestand, für die kein Lieferant mit gültigem Einkaufspreis hinterlegt ist */
    withoutSupplier: z.array(z.object({ itemId: zId, sku: z.string(), name: z.string(), available: z.int(), reorderQty: z.int(), stockUnit: zUnitInfo })),
    summary: z.object({
      /** Artikel mit verfügbarem Bestand unter dem Meldebestand */
      belowReorderCount: z.int(),
      /** davon durch offene Bestellungen, Umlagerungen oder Entwürfe gedeckt */
      coveredCount: z.int(),
      proposalCount: z.int(),
      supplierCount: z.int(),
      openRequisitionCount: z.int(),
      valueCents: z.int().optional(),
    }),
  }),
  permission: READ,
  risk: 'read',
  scope: (input) => [input.locationId],
  fieldPermissions: {
    'groups.*.lines.*.priceCents': PERMISSION_COSTS_READ,
    'groups.*.lines.*.pricePerQty': PERMISSION_COSTS_READ,
    'groups.*.lines.*.valueCents': PERMISSION_COSTS_READ,
    'groups.*.minOrderValueCents': PERMISSION_COSTS_READ,
    'groups.*.valueCents': PERMISSION_COSTS_READ,
    'groups.*.belowMinimum': PERMISSION_COSTS_READ,
    'groups.*.missingCents': PERMISSION_COSTS_READ,
    'groups.*.fillUp': PERMISSION_COSTS_READ,
    'groups.*.fillUpShort': PERMISSION_COSTS_READ,
    'summary.valueCents': PERMISSION_COSTS_READ,
  },
})

export const zCreatedOrder = z.object({
  id: zId,
  supplier: zSupplierRef,
  lineCount: z.int(),
  deliveryDate: z.string().nullable(),
  /** Warenwert netto ohne Pfand – nur mit „Einstandspreise sehen“ */
  netCents: z.int().optional(),
  belowMinimum: z.boolean().optional(),
})

export const suggestionOrder = defineContract({
  name: 'purchasing.suggestion.order',
  title: 'Bestellungen aus dem Vorschlag anlegen',
  description: 'Legt aus dem Bestellvorschlag je Lieferant eine Bestellung als Entwurf an – in einem Schritt.',
  llm: {
    description:
      'Macht aus dem Bestellvorschlag eines Standorts Bestellungen: je Lieferant EIN Entwurf mit Einkaufspreisen und ' +
      'Wunschtermin. Ohne `lines` wird der ganze Vorschlag übernommen (mit `supplierIds` nur diese ' +
      'Lieferanten); mit `lines` genau die genannten Artikel und Mengen (`orderQty` in Tausendstel der Bestelleinheit, ' +
      '1 Palette = 1000). Offene Anforderungen der Filialen zu diesen Artikeln gelten danach als bestellt. Es wird ' +
      'nichts an den Lieferanten gesendet: Die Entwürfe prüft der Einkauf und markiert sie mit purchasing.order.post ' +
      'als bestellt. Vorher den Vorschlag mit purchasing.suggestion.propose zeigen.',
    keywords: ['bestellen', 'Bestellung anlegen', 'Bestell das', 'Vorschlag übernehmen', 'nachbestellen'],
  },
  input: z.strictObject({
    locationId: zId,
    /** nur diese Lieferanten (ohne Angabe alle mit Vorschlag) – wirkt nur ohne `lines` */
    supplierIds: z.array(zId).max(50).optional(),
    lines: z
      .array(
        z.strictObject({
          itemId: zId,
          /** Menge in Tausendstel der Bestelleinheit des Lieferanten (1 Palette = 1000) */
          orderQty: zUnitQty,
        }),
      )
      .min(1, { error: 'Bitte wählen Sie mindestens einen Artikel.' })
      .max(500)
      .optional(),
    /** Wunschtermin für alle Bestellungen; ohne Angabe der früheste Liefertag des jeweiligen Lieferanten */
    deliveryDate: zDay.optional(),
    notes: z.string().trim().max(2000).nullish(),
  }),
  output: z.object({ orders: z.array(zCreatedOrder), message: z.string() }),
  permission: 'purchasing.order.write',
  risk: 'write',
  scope: (input) => [input.locationId],
  limits: { maxItems: 500, itemCount: (input) => input.lines?.length ?? 1 },
  fieldPermissions: { 'orders.*.netCents': PERMISSION_COSTS_READ, 'orders.*.belowMinimum': PERMISSION_COSTS_READ },
})

// ---- Anforderungen der Filialen -----------------------------------------------------------------------------------

export const REQUISITION_STATUSES = ['open', 'ordered', 'dismissed'] as const

export const zRequisitionRow = z.object({
  id: zId,
  location: zLocationRef,
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  unit: zUnitInfo,
  /** angeforderte Menge in Tausendstel der Basiseinheit */
  requestedQty: z.int(),
  status: z.enum(REQUISITION_STATUSES),
  statusLabel: z.string(),
  requestedBy: z.object({ id: zId.nullable(), name: z.string() }),
  requestedAt: z.date(),
  wantedDate: z.string().nullable(),
  note: z.string().nullable(),
  /** Bestellung, in der die Anforderung aufgegangen ist (Nummer erst nach dem Bestellen) */
  order: z.object({ id: zId, number: z.string().nullable(), statusLabel: z.string() }).nullable(),
  /** Grund der Ablehnung */
  answer: z.string().nullable(),
  own: z.boolean(),
})

export const requisitionCreate = defineContract({
  name: 'purchasing.requisition.create',
  title: 'Ware beim Einkauf anfordern',
  description: 'Meldet dem Einkauf Bedarf des eigenen Standorts. Die Anforderung erscheint im Bestellvorschlag.',
  llm: {
    description:
      'Fordert Ware beim Einkauf an (Filialleitung): Standort, Artikel und Menge im Gebinde (`qty` in Tausendstel, ' +
      '1 Karton = 1000; ohne `unitId` das Lagergebinde), optional Wunschtermin und Notiz. Bestellt wird damit NICHTS – ' +
      'die Anforderung erscheint im Bestellvorschlag des Einkaufs, der sie bestellt oder mit Grund ablehnt. Für Ware, ' +
      'die im eigenen Zentrallager liegt, ist die Umlagerung (inventory.transfer.request) der richtige Weg.',
    keywords: ['anfordern', 'Bedarf melden', 'Bestellwunsch', 'Einkauf bitten', 'brauche', 'Sonderbestellung'],
  },
  input: z.strictObject({
    locationId: zId,
    lines: z
      .array(z.strictObject({ itemId: zId, unitId: zId.nullish(), qty: zUnitQty }))
      .min(1, { error: 'Bitte nennen Sie mindestens einen Artikel.' })
      .max(100),
    wantedDate: zDay.nullish(),
    note: zNote,
  }),
  output: z.object({ ids: z.array(zId), message: z.string() }),
  permission: 'purchasing.requisition.create',
  risk: 'write',
  scope: (input) => [input.locationId],
  limits: { maxItems: 100, itemCount: (input) => input.lines.length },
})

export const requisitionList = defineContract({
  name: 'purchasing.requisition.list',
  title: 'Anforderungen anzeigen',
  description: 'Anforderungen der Standorte an den Einkauf mit ihrem Stand.',
  llm: {
    description:
      'Listet Anforderungen an den Einkauf an den Standorten, die der Nutzer sehen darf: Artikel, Menge (Tausendstel der ' +
      'Basiseinheit), wer wann angefordert hat und was daraus wurde (offen, bestellt mit Bestellung, abgelehnt mit ' +
      'Grund). Verwenden für „Ist meine Anforderung schon bestellt?“.',
    keywords: ['Anforderung', 'Bestellwunsch', 'angefordert', 'Stand meiner Anforderung'],
  },
  input: z.strictObject({
    locationId: zId.optional(),
    status: z.enum(REQUISITION_STATUSES).optional(),
    limit: z.int().min(1).max(200).default(100),
  }),
  output: z.object({ rows: z.array(zRequisitionRow) }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const requisitionDismiss = defineContract({
  name: 'purchasing.requisition.dismiss',
  title: 'Anforderung ablehnen',
  description: 'Lehnt eine offene Anforderung mit Grund ab; die anfordernde Person wird benachrichtigt.',
  llm: {
    description:
      'Lehnt eine offene Anforderung einer Filiale mit Grund ab (Einkauf). Sie verschwindet aus dem Bestellvorschlag, ' +
      'die anfordernde Person erhält eine Benachrichtigung mit dem Grund. Nur für offene Anforderungen.',
    keywords: ['Anforderung ablehnen', 'nicht bestellen', 'Bestellwunsch ablehnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: z.object({ id: zId }),
  permission: 'purchasing.suggestion.create',
  risk: 'write',
  scope: 'record',
})

export const requisitionWithdraw = defineContract({
  name: 'purchasing.requisition.withdraw',
  title: 'Eigene Anforderung zurückziehen',
  description: 'Zieht eine eigene, noch offene Anforderung zurück.',
  llm: {
    description: 'Zieht eine offene Anforderung zurück, die die Person selbst gestellt hat (vertippt, erledigt). Bereits bestellte Anforderungen lassen sich nicht zurückziehen.',
    keywords: ['Anforderung zurückziehen', 'Anforderung löschen', 'doch nicht'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({ id: zId }),
  permission: 'purchasing.requisition.create',
  risk: 'write',
  scope: 'record',
})
